import React, { Component } from 'react';
import { ToastManagerProp, withToastManager } from 'react-toast-notifications';

import classNames from 'classnames';
import { apiErrorHandler } from 'core/errors';
import { API, getCancelToken, request } from 'redux/api';
import { pluralize } from 'utils';

import ActivitiesIcon from 'assets/activities.png';
import AtRiskIcon from 'assets/at-risk.png';
import AttendanceRedIcon from 'assets/attendance-red.png';
import AttendanceSolidIcon from 'assets/attendance-solid.png';
import { ReactComponent as ChevronRightIcon } from 'assets/chevron_right.svg';
import CommentsIcon from 'assets/comments.png';

import CourseAnalyticsDetails from './CourseAnalyticsDetails';

interface GetCourseAnalyticsResponse extends MessageResponse {
  courses: CourseAnalyticsStats[];
}

type CourseAnalyticsProps = ToastManagerProp;

interface CourseAnalyticsState {
  isLoading: boolean;
  courses: CourseAnalyticsStats[];
  showCourseDetails: boolean;
  showCourseDetailsFor: CourseAnalyticsStats | null;
}

class CourseAnalytics extends Component<CourseAnalyticsProps, CourseAnalyticsState> {
  apiSources = getCancelToken();

  state: CourseAnalyticsState = {
    isLoading: true,
    courses: [],
    showCourseDetails: false,
    showCourseDetailsFor: null,
  };

  async getCourseAnalytics() {
    const { courses } = await request<GetCourseAnalyticsResponse>({
      url: API.COURSE_ANALYTICS,
      method: 'get',
      cancelToken: this.apiSources.token,
    });

    return courses;
  }

  async componentDidMount() {
    try {
      this.apiSources = getCancelToken();
      const courses = await this.getCourseAnalytics();
      this.setState({ courses, isLoading: false });
    } catch (err) {
      apiErrorHandler(this.props.toastManager, err, () => {
        this.setState({ isLoading: false });
      });
    }
  }

  componentWillUnmount() {
    this.apiSources.cancel();
  }

  getRow = (course: CourseAnalyticsStats) => {
    const badAttendance = course.avgAttendance < 60;
    return (
      <tr
        key={course.courseId}
        className='hovered clickable'
        onClick={() => this.setState({ showCourseDetails: true, showCourseDetailsFor: course })}
      >
        <td className='table__cell bold'>
          {course.courseCode} - {course.title}
        </td>
        <td className='table__cell table__cell--stats'>
          <div className='analytics-table__stats'>
            <img
              className='analytics-table__icon'
              src={badAttendance ? AttendanceRedIcon : AttendanceSolidIcon}
              alt=''
            />
            <span
              className={classNames('analytics-table__value', {
                'analytics-table__value--red': badAttendance,
              })}
            >
              {course.avgAttendance}%
            </span>
          </div>
        </td>
        <td className='table__cell table__cell--stats'>
          <div className='analytics-table__stats'>
            <img className='analytics-table__icon' src={ActivitiesIcon} alt='' />
            <span className='analytics-table__value'>{course.numActivities}</span>
          </div>
        </td>
        <td className='table__cell table__cell--stats'>
          <div className='analytics-table__stats'>
            <img className='analytics-table__icon' src={CommentsIcon} alt='' />
            <span className='analytics-table__value'>{course.numComments}</span>
          </div>
        </td>
        <td className='table__cell table__cell--stats'>
          {!!course.numAtRisk && (
            <div className='analytics-table__stats'>
              <img className='analytics-table__icon' src={AtRiskIcon} alt='' />
              <span className='analytics-table__value analytics-table__value--red'>{course.numAtRisk}</span>
            </div>
          )}
        </td>
        <td className='table__cell table__cell--more'>
          <ChevronRightIcon display='block' />
        </td>
      </tr>
    );
  };

  render() {
    const { isLoading, courses, showCourseDetails, showCourseDetailsFor } = this.state;
    return (
      <div className='section'>
        <div className='section__header'>
          <h3 className='section__title'>
            {isLoading
              ? 'Loading courses...'
              : courses.length
              ? `Insights available for ${courses.length} ${pluralize(courses.length, 'course')}`
              : 'Insights not available for any course'}
          </h3>
        </div>
        <div className='section__body'>
          <p className='section__tip'>
            Insights for any course are first available two weeks after the course has been published on
            Acadly
          </p>
          <table className='table table--bordered'>
            <tbody>{courses.map(this.getRow)}</tbody>
          </table>
        </div>
        <CourseAnalyticsDetails
          isOpen={showCourseDetails}
          course={showCourseDetailsFor}
          onClose={() => this.setState({ showCourseDetails: false })}
        />
      </div>
    );
  }
}

export default withToastManager(CourseAnalytics);
